import React, { useEffect, useState } from "react";
import { Container, Form, Row, Col, Button, Card } from "react-bootstrap";
import { Box } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import reportApi from "../../services/reportapi";
import branchApi from "../../services/branchapi";
import userApi from "../../services/userapi";
import Loader from "../loader/loader";
import DynamicBreadcrumb from "../header/breadcrumb";

const PickerPackerOrderTimeReport = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [branches, setBranches] = useState([]);
    const [users, setUsers] = useState([]);
    const [rows, setRows] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });

    const [filters, setFilters] = useState({
        from_date: moment().format("YYYY-MM-DD"),
        to_date: moment().format("YYYY-MM-DD"),
        branch_id: "",
        order_id: "",
        picker_user_id: "",
        packer_user_id: "",
        report_type: "",
    });

    const updateFilters = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const fetchBranches = async () => {
        const res = await branchApi.getAllBranches({
            limit: 200,
            page: 0,
            status: 1,
            sortModel: [{ field: "branchName", sort: "asc" }],
        });
        setBranches(res?.data?.result || []);
    };

    const fetchUsers = async () => {
        const res = await userApi.getUsers({
            limit: 200,
            page: 0,
            status: 1,
            userType: "",
            search: "",
            sortModel: [{ field: "name", sort: "asc" }],
        });

        const list = res?.data?.result || res?.data?.data?.result || [];
        setUsers(list);
    };

    const fetchReport = async (page = paginationModel.page, pageSize = paginationModel.pageSize) => {
        setLoading(true);
        try {
            const res = await reportApi.getPickerPackerOrderTime({
                ...filters,
                page,
                limit: pageSize,
            });

            const data = res?.data || {};
            setRows((data.rows || []).map((x, i) => ({ id: x.order_id || i + 1, ...x })));
            setTotalRecords(data.totalRecords || 0);
        } catch (err) {
            console.error("Picker packer order time report error:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBranches();
        fetchUsers();
    }, []);

    useEffect(() => {
        fetchReport(paginationModel.page, paginationModel.pageSize);
    }, [paginationModel.page, paginationModel.pageSize]);

    const handleFilter = () => {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
        fetchReport(0, paginationModel.pageSize);
    };

    const columns = [
        { field: "order_showid", headerName: "Order ID", flex: 1 },
        { field: "branch_name", headerName: "Branch", flex: 1.3 },
        { field: "picker_name", headerName: "Picker", flex: 1.2 },
        { field: "picking_started_at", headerName: "Picking Start", flex: 1.3 },
        { field: "picking_completed_at", headerName: "Picking End", flex: 1.3 },
        { field: "picking_minutes", headerName: "Picking Min", flex: 0.9 },
        { field: "picker_scan_count", headerName: "Picking Scans", flex: 0.9 },
        { field: "packer_name", headerName: "Packer", flex: 1.2 },
        { field: "packing_started_at", headerName: "Packing Start", flex: 1.3 },
        { field: "packing_completed_at", headerName: "Packing End", flex: 1.3 },
        { field: "packing_minutes", headerName: "Packing Min", flex: 0.9 },
        { field: "packer_scan_count", headerName: "Packing Scans", flex: 0.9 },
    ];

    return (
        <Container fluid className="mt-4 mb-4">
            <DynamicBreadcrumb pathArray={[{ name: "Order Wise Picking / Packing Time Report", url: null }]} />

            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>Order Wise Picking / Packing Time Report</h5>

                <Button
                    variant="outline-secondary"
                    onClick={() => navigate("/admin/reports/picker-packer-activity")}
                >
                    Back to Summary
                </Button>
            </div>

            <Form className="mb-3">
                <Row>
                    <Col md={3}>
                        <Form.Label>Branch</Form.Label>
                        <Form.Select
                            value={filters.branch_id}
                            onChange={(e) => updateFilters("branch_id", e.target.value)}
                        >
                            <option value="">All Branches</option>
                            {branches.map((branch) => (
                                <option key={branch.branchId} value={branch.branchId}>
                                    {branch.branchName}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>

                    <Col md={3}>
                        <Form.Label>From Date</Form.Label>
                        <Form.Control
                            type="date"
                            value={filters.from_date}
                            onChange={(e) => updateFilters("from_date", e.target.value)}
                        />
                    </Col>

                    <Col md={3}>
                        <Form.Label>To Date</Form.Label>
                        <Form.Control
                            type="date"
                            value={filters.to_date}
                            onChange={(e) => updateFilters("to_date", e.target.value)}
                        />
                    </Col>

                    <Col md={3}>
                        <Form.Label>Order ID</Form.Label>
                        <Form.Control
                            value={filters.order_id}
                            placeholder="Order ID"
                            onChange={(e) => updateFilters("order_id", e.target.value)}
                        />
                    </Col>
                </Row>

                <Row className="mt-3">
                    <Col md={3}>
                        <Form.Label>Report Type</Form.Label>
                        <Form.Select
                            value={filters.report_type}
                            onChange={(e) => updateFilters("report_type", e.target.value)}
                        >
                            <option value="">All</option>
                            <option value="PICKING">Picking Only</option>
                            <option value="PACKING">Packing Only</option>
                            <option value="BOTH">Both Picking & Packing</option>
                        </Form.Select>
                    </Col>

                    <Col md={3}>
                        <Form.Label>Picker User</Form.Label>
                        <Form.Select
                            value={filters.picker_user_id}
                            onChange={(e) => updateFilters("picker_user_id", e.target.value)}
                        >
                            <option value="">All Pickers</option>
                            {users.map((user) => (
                                <option key={user.userId} value={user.userId}>
                                    {user.name || user.userName}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>

                    <Col md={3}>
                        <Form.Label>Packer User</Form.Label>
                        <Form.Select
                            value={filters.packer_user_id}
                            onChange={(e) => updateFilters("packer_user_id", e.target.value)}
                        >
                            <option value="">All Packers</option>
                            {users.map((user) => (
                                <option key={user.userId} value={user.userId}>
                                    {user.name || user.userName}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>

                    <Col md={3} className="d-flex align-items-end">
                        <Button onClick={handleFilter}>Filter</Button>
                    </Col>
                </Row>
            </Form>

            {loading && <Loader />}

            <Card>
                <Card.Header>Order Wise Picking / Packing Time</Card.Header>
                <Card.Body>
                    <Box sx={{ width: "100%" }}>
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            autoHeight
                            paginationMode="server"
                            rowCount={totalRecords}
                            paginationModel={paginationModel}
                            onPaginationModelChange={setPaginationModel}
                            pageSizeOptions={[25, 50, 100]}
                            disableRowSelectionOnClick
                            slots={{ toolbar: GridToolbar }}
                        />
                    </Box>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default PickerPackerOrderTimeReport;